/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/RequirementsList/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import styles from "./RequirementsList.module.css";

import { generateAssetUrl } from "../../utils/common.js";
import AssetImage from "../AssetImage";
import { AppContext } from "../../context/AppContext";
import { getUser } from "../../logic/user";
import {
  getUserRoleData,
  getRequirements,
} from "../../logic/requirementsList/index.js";

import { ReactComponent as TitleIcon } from "../../assets/icons/RequirementsList/TitleIcon.svg";

import { use, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const RequirementsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { changePermissions } = use(AppContext);

  const [requirementsData, setRequirementsData] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!!token && token.trim() !== "undefined") {
      getData();
    } else {
      logger.info("Error trying to get the token to retrieve the users data.");
    }
  }, []);

  const getData = async () => {
    try {
      const userId = localStorage.getItem("userId");
      await getUserRoleData(userId, navigate, changePermissions);
      const requirements = await getRequirements(userId, navigate);
      setRequirementsData(requirements);
      await getCurrentUser();
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const getCurrentUser = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      return;
    }

    const userData = await getUser(userId, navigate);
    if (userData) {
      setCurrentUser(userData);
    }
  };

  const handleViewRequirement = (requirement) => {
    navigate("/requirements", {
      state: {
        selectedRequirement: requirement,
        openModal: true,
        currentUser: currentUser,
      },
    });
  };

  return (
    <div className={styles.requirementsListSection}>
      <div className={styles.requirementsListHeader}>
        <div className={styles.requirementsListHeaderTitle}>
          <TitleIcon className={styles.titleIcon} />
          <h2 className={styles.requirementsListTitle}>
            {t("components.requirementsList.title")}
          </h2>
        </div>
      </div>

      <div className={styles.requirementsList}>
        {requirementsData.length === 0 && (
          <div className={styles.noRequirements}>
            <p>{t("components.requirementsList.noRequirements")}</p>
          </div>
        )}
        {requirementsData.length > 0 &&
          requirementsData.map((requirement) => (
            <div
              key={
                requirement._id ||
                requirement.id ||
                `${requirement.titulo}-${requirement.nombreEmpresa}-${requirement.fechaLimite}`
              }
              className={styles.requirementCard}
            >
              <div className={styles.requirementCardImage}>
                <AssetImage
                  src={generateAssetUrl(
                    `requirement/sectorImages/${requirement.sector}.png`,
                  )}
                  alt={t("common.categoryAlt")}
                />
              </div>
              <div className={styles.requirementCardInfo}>
                <span className={styles.companyName}>
                  {requirement.nombreEmpresa}
                </span>
                <span className={styles.requirementTitle}>
                  {requirement.titulo}
                </span>
                <div className={styles.requirementDetails}>
                  <span className={styles.requirementDueDate}>
                    {dayjs(requirement.fechaLimite).utc().format("DD/MM/YYYY")}
                  </span>
                </div>
                <div className={styles.categoryTag}>
                  <span>{requirement.nombreCategoria}</span>
                </div>
                <div className={styles.buttonsContainer}>
                  <div
                    className={styles.viewRequirementButton}
                    onClick={() => handleViewRequirement(requirement)}
                  >
                    {t("pages.requirements.viewRequirement")}
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default RequirementsList;
